import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Add Recording Modal ───────────────────────────────────────────────────────

const AddRecordingModal = ({ batches, onSave, onCancel, loading }) => {
  const [form, setForm] = useState({ batch_id: '', class_id: '', title: '', youtube_link: '' });
  const [classes, setClasses] = useState([]);
  const [formError, setFormError] = useState('');
  const titleRef = useRef(null);

  useEffect(() => { titleRef.current?.focus(); }, []);

  useEffect(() => {
    if (!form.batch_id) { setClasses([]); setForm((p) => ({ ...p, class_id: '' })); return; }
    api.get('/api/classes', { params: { batch_id: form.batch_id } })
      .then((res) => setClasses(res.data || []))
      .catch(() => setClasses([]));
  }, [form.batch_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setFormError('Title is required.');
    if (!form.youtube_link.trim()) return setFormError('YouTube link is required.');
    if (!form.batch_id && !form.class_id) return setFormError('Select at least a batch.');
    onSave({
      title: form.title.trim(),
      youtube_link: form.youtube_link.trim(),
      batch_id: form.batch_id || null,
      class_id: form.class_id || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Add Recording</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
            <input ref={titleRef} type="text" name="title" value={form.title} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Session 3 – React Hooks Deep Dive" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">YouTube Link</label>
            <input type="url" name="youtube_link" value={form.youtube_link} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="https://youtube.com/watch?v=..." required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch</label>
            <select name="batch_id" value={form.batch_id} onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
              <option value="">Select batch</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
            </select>
          </div>
          {form.batch_id && classes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Link to Class <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select name="class_id" value={form.class_id} onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white">
                <option value="">No specific class</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onCancel} disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50">
              {loading ? 'Adding...' : 'Add Recording'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  const styles = { success: 'bg-emerald-50 border-emerald-200 text-emerald-800', error: 'bg-red-50 border-red-200 text-red-800' };
  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const TrainerRecordings = () => {
  const [recordings, setRecordings] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchAll = useCallback(async () => {
    try {
      const [recRes, batchRes] = await Promise.all([
        api.get('/api/recordings'),
        api.get('/api/batches'),
      ]);
      setRecordings(recRes.data || []);
      setBatches(batchRes.data || []);
    } catch {
      setError('Failed to load recordings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAdd = async (form) => {
    setAddLoading(true);
    try {
      await api.post('/api/recordings', form);
      setShowAdd(false);
      showToast('Recording added successfully');
      await fetchAll();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to add recording'), 'error');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recording?')) return;
    try {
      await api.delete(`/api/recordings/${id}`);
      setRecordings((prev) => prev.filter((r) => r.id !== id));
      showToast('Recording deleted');
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to delete recording'), 'error');
    }
  };

  const filtered = recordings.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.title || '').toLowerCase().includes(q) ||
      (r.class_title || '').toLowerCase().includes(q) ||
      (r.batch_name || '').toLowerCase().includes(q) ||
      (r.uploader_name || '').toLowerCase().includes(q)
    );
  });

  // Group by batch
  const grouped = {};
  filtered.forEach((r) => {
    const key = r.batch_name || 'General';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="skeleton h-8 w-48" />
          <div className="bg-white rounded-2xl border border-gray-200">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-4 flex-1">
                  <div className="skeleton w-9 h-9 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-4 w-48" />
                    <div className="skeleton h-3 w-64" />
                  </div>
                </div>
                <div className="skeleton h-9 w-36 rounded-xl" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">Retry</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Recordings</h1>
              <p className="text-sm text-gray-500">{recordings.length} recordings</p>
            </div>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Recording
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, batch, class or uploader..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
          />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-14 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No recordings yet</p>
              <p className="text-xs text-gray-400 mt-1">
                {search ? 'Try a different search term' : 'Click "Add Recording" to post a YouTube link for your students'}
              </p>
            </div>
          </div>
        ) : (
          Object.entries(grouped).map(([batchName, items]) => (
            <div key={batchName}>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{batchName}</h2>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
                {items.map((rec) => (
                  <div key={rec.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors">
                    {/* Play icon */}
                    <div className="w-9 h-9 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>

                    {/* Info block */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{rec.title}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                        {rec.class_title && (
                          <span className="text-xs text-gray-500 truncate">{rec.class_title}</span>
                        )}
                        {rec.uploader_name && (
                          <span className="text-xs text-gray-400">by {rec.uploader_name}</span>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(rec.created_at)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={rec.youtube_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-xl hover:bg-secondary/90 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Watch
                      </a>
                      <button
                        onClick={() => handleDelete(rec.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete recording"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {showAdd && (
        <AddRecordingModal
          batches={batches}
          onSave={handleAdd}
          onCancel={() => setShowAdd(false)}
          loading={addLoading}
        />
      )}
    </Layout>
  );
};

export default TrainerRecordings;
